"use client";

import { opinionsFor } from "@kedaat/definitions";
import { formatTime, type PlainDate, type ZmanimEvaluator } from "@kedaat/engine";
import type { Locality } from "@kedaat/locations";
import { useEffect, useRef } from "react";
import type { ZmanRow } from "@/lib/zmanim";

const usesElevation = (calc: unknown) => JSON.stringify(calc).includes('"elevation"');

function formatDifference(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes === 0) return "אותו זמן";
  return `${minutes > 0 ? "+" : "−"}${Math.abs(minutes)} דק׳`;
}

/** Compare every opinion for one zman, read how each is calculated, and choose one. */
export function OpinionDialog({
  row,
  profileOpinionId,
  evaluator,
  date,
  locality,
  seconds,
  onChoose,
  onClose,
}: {
  row: ZmanRow | null;
  profileOpinionId: string | null;
  evaluator: ZmanimEvaluator | null;
  date: PlainDate;
  locality: Locality;
  seconds: boolean;
  onChoose: (opinionId: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (row && !dialog.open) dialog.showModal();
    if (!row && dialog.open) dialog.close();
  }, [row]);

  const options =
    row && evaluator
      ? opinionsFor(row.zman.id)
          .map((opinion) => ({ opinion, ms: evaluator.evaluate(opinion.calc, date) }))
          .sort((a, b) => (a.ms ?? Infinity) - (b.ms ?? Infinity))
      : [];

  // A small timeline placing every opinion by its time, earliest on the right.
  const timed = options.filter(
    (o): o is { opinion: (typeof o)["opinion"]; ms: number } => o.ms !== null,
  );
  const earliest = timed[0];
  const latest = timed.at(-1);
  const span = earliest && latest ? latest.ms - earliest.ms : 0;

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby="opinion-dialog-title"
      className="mx-auto mb-0 mt-auto max-h-[85dvh] w-full max-w-lg rounded-t-3xl bg-card p-0 text-ink shadow-2xl backdrop:bg-black/50 sm:mb-auto sm:rounded-3xl"
    >
      {row && (
        <div className="flex max-h-[85dvh] flex-col">
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <h2 id="opinion-dialog-title" className="font-serif text-2xl font-bold">
                {row.zman.label.he}
              </h2>
              <p className="text-sm text-muted">השוואת השיטות ובחירת שיטה לזמן זה</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="סגירה"
              className="inline-flex size-10 items-center justify-center rounded-full text-2xl leading-none text-muted hover:bg-accent-soft"
            >
              ×
            </button>
          </header>

          {span > 0 && earliest && latest && (
            <div className="border-b border-line px-6 pb-3 pt-4" aria-hidden="true">
              <div className="relative h-6">
                <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-line" />
                {timed.map(({ opinion, ms }) => {
                  const selected = opinion.id === row.opinion.id;
                  return (
                    <span
                      key={opinion.id}
                      title={`${opinion.label.he} ${formatTime(ms, opinion.rounding)}`}
                      className={`absolute top-1/2 rounded-full ${
                        selected ? "z-10 size-4 bg-accent ring-4 ring-card" : "size-2.5 bg-muted/70"
                      }`}
                      style={{
                        insetInlineStart: `${((ms - earliest.ms) / span) * 100}%`,
                        transform: "translate(50%, -50%)",
                      }}
                    />
                  );
                })}
              </div>
              <div className="mt-1 flex justify-between text-xs text-muted tabular-nums">
                <span>{formatTime(earliest.ms, earliest.opinion.rounding)}</span>
                <span>{formatTime(latest.ms, latest.opinion.rounding)}</span>
              </div>
            </div>
          )}

          <div role="radiogroup" aria-label="שיטות" className="overflow-y-auto px-3 py-3">
            {options.map(({ opinion, ms }) => {
              const selected = opinion.id === row.opinion.id;
              const isProfileDefault = opinion.id === profileOpinionId;
              return (
                <button
                  key={opinion.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onChoose(opinion.id)}
                  className={`mb-2 block w-full rounded-2xl border px-4 py-3 text-start transition-colors ${
                    selected
                      ? "border-accent bg-accent-soft"
                      : "border-line hover:bg-accent-soft/50"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">
                      {opinion.label.he}
                      {isProfileDefault && (
                        <span className="ms-2 rounded-full border border-line px-2 py-0.5 text-xs font-normal text-muted">
                          ברירת המחדל בשיטה
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-end">
                      <span className="block text-xl font-bold tabular-nums">
                        {ms === null ? "—" : formatTime(ms, opinion.rounding, { seconds })}
                      </span>
                      {!selected && ms !== null && row.ms !== null && (
                        <span className="block text-xs text-muted tabular-nums">
                          {formatDifference(ms - row.ms)}
                        </span>
                      )}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted">
                    {opinion.explanation.he}
                  </p>
                  {usesElevation(opinion.calc) && !locality.elevationShkiaEligible && (
                    <p className="mt-1 text-xs text-warn">
                      ביישוב זה החישוב נעשה לפי גובה פני הים, כי הוא אינו מסומן כגבוה מסביבתו.
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </dialog>
  );
}
